import React, { Fragment, useState, useEffect } from 'react';
import { useRefresh, Button, useListContext, useResourceDefinition, useDataProvider } from 'react-admin';
import { apiUrl, httpClient } from '../../dataProvider';
import MoveIcon from '@mui/icons-material/SwapHoriz';
import CheckIcon from '@mui/icons-material/Check';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import { Typography, TextField, Autocomplete, Alert } from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import useCustomNotify from '../useCustomNotify';

const MoveTimelogsAction = (props) => {
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [targetProject, setTargetProject] = useState(null);
    const [projects, setProjects] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [validationResult, setValidationResult] = useState(null);
    const [error, setError] = useState(null);
    const [selectedTimelogs, setSelectedTimelogs] = useState([]);
    const [workerId, setWorkerId] = useState(null);
    const [loadingTimelogs, setLoadingTimelogs] = useState(false);
    const [loadingProjects, setLoadingProjects] = useState(false);
    
    const notify = useCustomNotify();
    const refreshView = useRefresh();
    const dataProvider = useDataProvider();
    const { selectedIds } = useListContext();
    const resource = useResourceDefinition();

    // Fetch selected timelogs when the dialog opens or selectedIds changes
    useEffect(() => {
        if (open && selectedIds.length > 0) {
            loadSelectedTimelogs();
        }
    }, [open, selectedIds]);

    // Load initial projects when the dialog opens
    useEffect(() => {
        if (open) {
            loadProjects();
        }
    }, [open]);

    const loadProjects = (searchFilter = '') => {
        setLoadingProjects(true);
        
        // Use the 'search' parameter which is used in ProjectList
        const filter = searchFilter 
            ? { search: searchFilter }
            : {};
            
        dataProvider.getList('projects', { 
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'name_internal', order: 'ASC' },
            filter
        })
        .then(({ data }) => {
            setProjects(data);
        })
        .catch((error) => {
            notify(`Error loading projects: ${error.message}`, { type: 'warning' });
        })
        .finally(() => {
            setLoadingProjects(false);
        });
    };

    const loadSelectedTimelogs = () => {
        if (selectedIds.length === 0) return;
        
        setLoadingTimelogs(true);
        setError(null);
        
        dataProvider.getMany('timelogs', { ids: selectedIds })
            .then(({ data }) => {
                setSelectedTimelogs(data);
                
                // Check if all timelogs have the same worker
                if (data.length > 0) {
                    const workers = [...new Set(data.map(timelog => timelog.worker))];
                    
                    if (workers.length > 1) {
                        setError({
                            message: 'Cannot move timelogs from different workers. Please select timelogs from the same worker only.',
                            status: 'error'
                        });
                        setWorkerId(null);
                    } else {
                        setWorkerId(workers[0]);
                    }
                }
            })
            .catch((error) => {
                notify(`Error loading timelogs: ${error.message}`, { type: 'warning' });
                setError({
                    message: `Error loading timelogs: ${error.message}`,
                    status: 'error'
                });
            })
            .finally(() => {
                setLoadingTimelogs(false);
            });
    };

    const handleOpen = () => {
        setOpen(true);
        setValidationResult(null);
        setError(null);
        setWorkerId(null);
        setSelectedTimelogs([]);
        setSearchText('');
    };

    const handleClose = () => {
        setOpen(false);
        setValidationResult(null);
        setError(null);
        setTargetProject(null);
        setWorkerId(null);
        setSelectedTimelogs([]);
        setSearchText('');
    };

    const handleSearchChange = (event, newValue) => {
        setSearchText(newValue);
        loadProjects(newValue);
    };

    const handleProjectChange = (event, value) => {
        setTargetProject(value);
        setValidationResult(null);
        setError(null);
    };

    const handleValidate = () => {
        if (!targetProject || !targetProject.id) {
            notify('Please select a target project', { type: 'warning' });
            return;
        }

        if (selectedIds.length === 0) {
            notify('Please select at least one timelog to move', { type: 'warning' });
            return;
        }

        if (!workerId) {
            notify('Worker ID not found or timelogs from different workers selected', { type: 'warning' });
            return;
        }

        setLoading(true);
        setError(null);
        
        // Call the validate endpoint using the correct URL pattern
        const url = `${apiUrl}/timelogs/validate-timelogs-move/`;
        
        httpClient(url, { 
            method: 'POST',
            body: JSON.stringify({
                timelog_ids: selectedIds,
                project_id: targetProject.id,
                worker_id: workerId
            })
        })
        .then((response) => {
            const result = response.json;
            setValidationResult(result);
        })
        .catch((error) => {
            // Handle different error responses
            if (error.body && error.body.message) {
                setError({
                    message: error.body.message,
                    status: error.status
                });
            } else {
                setError({
                    message: `Error validating timelog move: ${error.message}`,
                    status: error.status
                });
            }
        })
        .finally(() => {
            setLoading(false);
        });
    };

    const handleMove = () => {
        if (!validationResult || !validationResult.data) {
            notify('Please validate the timelog move first', { type: 'warning' });
            return;
        }

        setLoading(true);
        setError(null);
        
        // Call the execute endpoint
        const url = `${apiUrl}/timelogs/execute-timelogs-move/`;
        
        httpClient(url, { 
            method: 'POST',
            body: JSON.stringify({
                timelog_ids: validationResult.data.timelog_ids,
                project_id: validationResult.data.project_id,
                worker_id: validationResult.data.worker_id
            })
        })
        .then((response) => {
            const result = response.json;
            notify(`Successfully moved ${result.data.moved_timelogs_count} timelogs from project ${result.data.from_project_id} to project ${result.data.to_project_id}`, { type: 'success' });
            handleClose();
            refreshView();
        })
        .catch((error) => {
            // Handle different error responses
            if (error.body && error.body.message) {
                setError({
                    message: error.body.message,
                    status: error.status
                });
            } else {
                setError({
                    message: `Error executing timelog move: ${error.message}`,
                    status: error.status
                });
            }
        })
        .finally(() => {
            setLoading(false);
        });
    };

    // Determine if we're in validation or move mode
    const isValidated = validationResult && validationResult.status === "valid";

    return (
        <Fragment>
            <Button label="Move Timelogs" onClick={handleOpen}>
                <MoveIcon />
            </Button>
            <Dialog 
                open={open} 
                onClose={handleClose} 
                aria-labelledby="move-dialog-title"
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle id="move-dialog-title">Move Timelogs</DialogTitle>
                <DialogContent>
                    {loadingTimelogs && (
                        <Alert 
                            severity="info" 
                            sx={{ mb: 2 }}
                            icon={<CircularProgress size={20} />}
                        >
                            Loading selected timelogs...
                        </Alert>
                    )}
                    
                    {error && (
                        <Alert 
                            severity="error" 
                            sx={{ mb: 2 }}
                        >
                            {error.message}
                        </Alert>
                    )}
                    
                    {isValidated && (
                        <Alert 
                            severity="success" 
                            sx={{ mb: 2 }}
                        >
                            {validationResult.message}
                        </Alert>
                    )}

                    {isValidated && validationResult.data.different_rate && (
                        <Alert 
                            severity="warning" 
                            sx={{ mb: 2 }}
                        >
                            Warning: The target project has a different hourly rate than the current project.
                        </Alert>
                    )}

                    {selectedIds.length > 0 && workerId && (
                        <Alert 
                            severity="info" 
                            sx={{ mb: 2 }}
                        >
                            {`Selected ${selectedIds.length} timelog${selectedIds.length > 1 ? 's' : ''}`}
                        </Alert>
                    )}

                    <Typography variant="body1" gutterBottom>
                        Select a target project to move the selected timelogs to:
                    </Typography>
                    <Autocomplete
                        options={projects}
                        getOptionLabel={(option) => option.name_internal || ''}
                        style={{ width: '100%', marginTop: 16 }}
                        onChange={handleProjectChange}
                        onInputChange={handleSearchChange}
                        disabled={isValidated || !workerId}
                        value={targetProject}
                        loading={loadingProjects}
                        renderInput={(params) => (
                            <TextField 
                                {...params} 
                                label="Target Project" 
                                variant="outlined" 
                                fullWidth 
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {loadingProjects ? <CircularProgress size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                </DialogContent>
                <DialogActions>
                    <Button label="Cancel" onClick={handleClose} />
                    
                    {!isValidated ? (
                        <Button 
                            label="Validate" 
                            onClick={handleValidate} 
                            disabled={!targetProject || !targetProject.id || loading || loadingTimelogs || !workerId}
                        >
                            {loading ? <CircularProgress size={24} /> : <CheckIcon />}
                        </Button>
                    ) : (
                        <Button 
                            label="Move" 
                            onClick={handleMove} 
                            disabled={loading}
                        >
                            {loading ? <CircularProgress size={24} /> : <MoveIcon />}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Fragment>
    );
};

export default MoveTimelogsAction;